import type { MenuItem } from '@/cms/fields/types/common'
import type { NavigationProps } from './types'
import { createHeaderClickEvent } from '@/domain/header/analytics'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import * as Portal from '@radix-ui/react-portal'
import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { focusWithinOutline, Icon } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import { styled } from 'styled-components'
import { Logo, LogoCompact } from '../Logo'
import { HeaderLink } from './HeaderLink'
import { Menu, MenuTrigger, NestedSubMenu, SubMenu } from './Menu'
import { Navbar } from './Navbar'

const NavigationMenuViewport = styled(NavigationMenu.Viewport)`
  border-top: 1px solid ${tok('semantics_dividers_color')};

  &[data-state='closed'] {
    display: none;
  }
`

const ScreenReaderOnlyText = styled.span`
  clip: rect(0 0 0 0);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const ActiveContent = styled.div`
  background: ${tok('semantics_views_background_color')};

  left: 0;
  margin: 0;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1;
`

const Overlay = styled.div`
  background: ${tok('components_backdrop_color')};
  height: 100vh;
  left: 0;
  position: absolute;
  width: 100vw;
`

const HeaderLogo = styled.div`
  ${Logo} {
    display: none;
  }
  ${LogoCompact} {
    display: inline;
  }
  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    ${LogoCompact} {
      display: none;
    }
    ${Logo} {
      display: block;
    }
  }
`

export const DesktopNavbarItem = styled(Navbar.Item)`
  &:not(:first-of-type) {
    display: none;
    @media (min-width: ${tokPx('primitives_breakpoint_l_min')}) {
      display: block;
    }
  }

  &:nth-of-type(2) {
    margin-left: ${space('primitives_space_8')};
  }

  & > ${MenuTrigger}[data-state='open'] {
    background: ${tok('primitives_color_neutral_100')};

    ${Icon} {
      transform: rotate(180deg);
    }
  }
`

const NavbarItemWithFocus = styled(Navbar.Item)`
  ${focusWithinOutline()}//This is to enable the logo to have our focus outline when it is focused
`

export function DesktopNavigation({ data, extraData, headerWrapperElement }: NavigationProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const [openSubmenuId, setOpenSubmenuId] = React.useState('')

  const handleMenuItemClick = (result: string[], children: MenuItem[] | undefined) => {
    sendEvent(
      createHeaderClickEvent({
        result,
        hasChildren: children && children.length > 0,
      }),
    )
  }

  return (
    <>
      <Navbar value={openSubmenuId} onValueChange={setOpenSubmenuId}>
        <Navbar.List>
          <NavbarItemWithFocus>
            <NavigationMenu.Link href="/" data-qa="header-logo-link">
              <HeaderLogo>
                <Logo />
                <LogoCompact />
              </HeaderLogo>
              <ScreenReaderOnlyText>{intl.formatMessage({ id: 'link_home' })}</ScreenReaderOnlyText>
            </NavigationMenu.Link>
          </NavbarItemWithFocus>
          {data && (
            <DesktopNavbarItem>
              <MenuTrigger
                onClick={() => handleMenuItemClick([data.text], data.children)}
                onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                onPointerLeave={(e: React.PointerEvent) => e.preventDefault()}
              >
                {data.text}
                <Icon glyph="ChevronDownExtraSmall" />
              </MenuTrigger>
              <Menu>
                {Boolean(openSubmenuId) && <RemoveScrollBar />}
                {data.children?.map((menuItem, menuItemKey) => (
                  <Menu.Item
                    onClick={() => handleMenuItemClick([data.text, menuItem.text], menuItem.children)}
                    key={menuItemKey}
                  >
                    <SubMenu label={menuItem.text}>
                      {menuItem.children?.map((subMenuItem, subMenuItemKey) => (
                        <SubMenu.Item
                          onClick={() =>
                            handleMenuItemClick([data.text, menuItem.text, subMenuItem.text], subMenuItem.children)}
                          key={subMenuItemKey}
                        >
                          {subMenuItem.children && subMenuItem.children.length > 0
                            ? (
                                <NestedSubMenu label={subMenuItem.text}>
                                  {subMenuItem.children.map((nestedSubMenuItem, nestedSubMenuItemKey) => (
                                    <HeaderLink
                                      onClick={() =>
                                        handleMenuItemClick(
                                          [data.text, menuItem.text, subMenuItem.text, nestedSubMenuItem.text],
                                          nestedSubMenuItem.children,
                                        )}
                                      key={nestedSubMenuItemKey}
                                      href={nestedSubMenuItem.href}
                                    >
                                      {nestedSubMenuItem.text}
                                    </HeaderLink>
                                  ))}
                                </NestedSubMenu>
                              )
                            : (
                                <HeaderLink
                                  onClick={() =>
                                    handleMenuItemClick([data.text, menuItem.text, subMenuItem.text], subMenuItem.children)}
                                  href={subMenuItem.href}
                                >
                                  {subMenuItem.text}
                                </HeaderLink>
                              )}
                        </SubMenu.Item>
                      ))}
                    </SubMenu>
                  </Menu.Item>
                ))}
              </Menu>
            </DesktopNavbarItem>
          )}
          {extraData?.map((menuItem, menuItemKey) => (
            <DesktopNavbarItem key={menuItemKey}>
              <HeaderLink onClick={() => handleMenuItemClick([menuItem.text], undefined)} href={menuItem.href}>
                {menuItem.text}
              </HeaderLink>
            </DesktopNavbarItem>
          ))}
        </Navbar.List>

        {/* Places the active submenu on the bottom of the header */}
        <ActiveContent>
          <NavigationMenuViewport forceMount />
        </ActiveContent>
      </Navbar>
      {openSubmenuId && (
        <Portal.Root container={headerWrapperElement}>
          <Overlay />
        </Portal.Root>
      )}
    </>
  )
}
